import React from 'react';

import { App } from '@flipdish/api-client-typescript';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { checkVisibility } from 'react-redux-permissions/dist/core';
import { Route, RouteComponentProps, Switch } from 'react-router';
import { compose } from 'recompose';

import LazyComponent from '@fd/ui/LazyComponent';

import { toKebabCase } from '../../helpers/strings';
import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { flagService } from '../../services/flagService';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';
import CustomersPage from './CustomersPage';
import CustomerDetails from './Details/CustomerDetails';

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));
const ImportCustomers = lazyWithRetry(() => import('./ImportCustomers'));

type Props = MappedProps & RouteComponentProps;

const CustomerRoutes: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    appId,
    translate,
    match: { path },
    canEdit,
    canImport,
    isImportCustomersEnabled,
  } = props;
  return (
    <ErrorBoundary identifier="customers">
      <Switch>
        {isImportCustomersEnabled && canImport && (
          <Route
            exact
            path={`${path}/import`}
            render={() => (
              <LazyComponent>
                <ImportCustomers />
              </LazyComponent>
            )}
          />
        )}
        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.IMPORT_CUSTOMERS)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                toParent={`/${appId}/customers?tab=rms`}
                subscription={RMSSubscriptions.BASE}
                title={translate('Import_Customers')}
                module={RMSModules.IMPORT_CUSTOMERS}
                url={`modules/marketing/${RMSModules.IMPORT_CUSTOMERS}`}
              />
            </LazyComponent>
          )}
        />
        {canEdit && <Route exact path={`${path}/:customerId`} component={CustomerDetails} />}
        <Route path={`${path}`} component={CustomersPage} />
      </Switch>
    </ErrorBoundary>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const canEdit = checkVisibility(
    state.permissions,
    [App.AppResourceSetEnum.ViewCustomerStatistics],
    []
  );
  const canImport = checkVisibility(
    state.permissions,
    ['Owner', 'ManagedOwner', 'FlipdishStaff'],
    []
  );
  return {
    appId: state.currentApp?.AppId,
    canEdit,
    canImport,
    isImportCustomersEnabled: flagService.isFlagOn(state, 'importCustomers'),
    translate: getTranslate(state.locale),
  };
};

export default compose<Props, RouteComponentProps>(connect(mapStateToProps))(CustomerRoutes);
